/**
 * Theme toggle component - segmented control
 *
 * Three options: Light / System / Dark
 * Syncs with marketing site using same localStorage key (ceetrix-theme).
 */

import { useState, useEffect, useCallback } from 'react';
import './ThemeSwitcher.css';

const STORAGE_KEY = 'ceetrix-theme';
type Theme = 'light' | 'system' | 'dark';

function getStoredTheme(): Theme | null {
  if (typeof window === 'undefined') return null;
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  return null;
}

function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  root.classList.remove('light', 'dark');

  if (theme === 'light') {
    root.classList.add('light');
  } else if (theme === 'dark') {
    root.classList.add('dark');
  }
  // 'system' = no class, let media query handle it
}

export function ThemeSwitcher() {
  const [theme, setTheme] = useState<Theme>(() => {
    return getStoredTheme() ?? 'system';
  });

  // Apply theme on mount and when changed
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const handleSelect = useCallback((newTheme: Theme) => {
    setTheme(newTheme);
    localStorage.setItem(STORAGE_KEY, newTheme);
  }, []);

  return (
    <div className="theme-switcher" role="radiogroup" aria-label="Theme">
      <button
        className={`theme-switcher__option${theme === 'light' ? ' theme-switcher__option--active' : ''}`}
        onClick={() => handleSelect('light')}
        aria-label="Light mode"
        aria-checked={theme === 'light'}
        role="radio"
        title="Light mode"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="5" />
          <line x1="12" y1="1" x2="12" y2="3" />
          <line x1="12" y1="21" x2="12" y2="23" />
          <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
          <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
          <line x1="1" y1="12" x2="3" y2="12" />
          <line x1="21" y1="12" x2="23" y2="12" />
          <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
          <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
        </svg>
      </button>
      <button
        className={`theme-switcher__option${theme === 'system' ? ' theme-switcher__option--active' : ''}`}
        onClick={() => handleSelect('system')}
        aria-label="System preference"
        aria-checked={theme === 'system'}
        role="radio"
        title="System preference"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
          <line x1="8" y1="21" x2="16" y2="21" />
          <line x1="12" y1="17" x2="12" y2="21" />
        </svg>
      </button>
      <button
        className={`theme-switcher__option${theme === 'dark' ? ' theme-switcher__option--active' : ''}`}
        onClick={() => handleSelect('dark')}
        aria-label="Dark mode"
        aria-checked={theme === 'dark'}
        role="radio"
        title="Dark mode"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
        </svg>
      </button>
    </div>
  );
}
